// 📌 Utility function to filter driver races based on playoffs
export const filterDriverRaces = (raceData, driver, excludePlayoffs, excludeDnf) => {
  return raceData.filter(
    (entry) =>
      entry.driver_name === driver &&
      (!excludePlayoffs || (entry.season_stage === "season" || (entry.season_stage && !entry.season_stage.includes('playoff') && entry.season_stage !== 'chase'))) &&
      (!excludeDnf || entry.status === 'finished')
  );
};

export const filterTeamRaces = (raceData, team, raceDates, excludePlayoffs, excludeDnf) => {
  return raceData.filter(
    (entry) =>
      entry.team_name === team &&
      (Array.isArray(raceDates) ? raceDates.includes(entry.race_date) : entry.race_date === raceDates) &&
      (!excludePlayoffs || (entry.season_stage === "season" || (entry.season_stage && !entry.season_stage.includes('playoff') && entry.season_stage !== 'chase'))) &&
      (!excludeDnf || entry.status === 'finished')
  );
};

// 📌 Utility function to calculate average feature value
export const getAverageFeatureValue = (raceData, driver, excludePlayoffs, excludeDnf, feature) => {
  var totalFeatureValue;
  const driverRaces = filterDriverRaces(raceData, driver, excludePlayoffs, excludeDnf);
  if (driverRaces.length === 0) return "-";
  if (feature === "fantasy_points") {
    totalFeatureValue = driverRaces.reduce((sum, race) => sum + (race.race_finish_points || 0) + (race.race_stage_points || 0), 0);
  } else {
    totalFeatureValue = driverRaces.reduce((sum, race) => sum + (race[feature] || 0), 0);
  }    
  return (totalFeatureValue / driverRaces.length).toFixed(2);
};

export const getCleanAverageFeatureValue = ( feature, races ) => {
  var totalFeatureValue;
  if (races.length === 0) return "-";
  if (feature === "fantasy_points") {
    totalFeatureValue = races.reduce((sum, race) => sum + (race.race_finish_points || 0) + (race.race_stage_points || 0), 0);
  } else {
    totalFeatureValue = races.reduce((sum, race) => sum + (race[feature] || 0), 0);
  }    
  return (totalFeatureValue / races.length).toFixed(2);
}

// 📌 Utility function to get a driver's finish position in a specific race
export const getFeatureValue = (raceData, driver, excludePlayoffs, excludeDnf, feature) => {
  var feature_value;
  const raceEntry = raceData.find(
    (entry) =>
      entry.driver_name === driver &&
      (!excludePlayoffs || (entry.season_stage === "season" || (entry.season_stage && !entry.season_stage.includes('playoff') && entry.season_stage !== 'chase'))) &&
      (!excludeDnf || entry.status === 'finished')
  );
  if (!raceEntry) return { value: "-", status: 'finished' };
  if (feature === "fantasy_points") {
    feature_value = (raceEntry?.race_finish_points ?? 0) + (raceEntry?.race_stage_points ?? 0);
  } else {
    feature_value = raceEntry?.[feature] ?? null;
  }    
  return { value: feature_value, status: raceEntry.status };
};

export const getTeamFeatureValue = (raceData, team, raceDate, excludePlayoffs, excludeDnf, feature) => {
  var totalFeatureValue;
  const teamRaces = filterTeamRaces(raceData, team, raceDate, excludePlayoffs, excludeDnf);
  if (teamRaces.length === 0) return "-";
  if (feature === "fantasy_points") {
    totalFeatureValue = teamRaces.reduce((sum, race) => sum + (race.race_finish_points || 0) + (race.race_stage_points || 0), 0);
  } else {
    totalFeatureValue = teamRaces.reduce((sum, race) => sum + (race[feature] || 0), 0);
  }    
  return (totalFeatureValue / teamRaces.length).toFixed(2);
};

// 📌 Utility function to format dates as "Spring/Fall YEAR"
export const getSeasonLabel = (dateString) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;

  if (month < 6) return `Spring ${year}`;
  if (month < 9) return `Summer ${year}`;
  return `Fall ${year}`;
};

export const getRankInGroup = (feature, driver, driverAverages) => {
  const sortedDrivers = [...driverAverages].sort((a, b) => {
    const valA = a[feature];
    const valB = b[feature];
    const isInvalidA = valA === "-" || isNaN(valA);
    const isInvalidB = valB === "-" || isNaN(valB);
    if (isInvalidA && isInvalidB) return 0; // Keep order if both are "-"
    if (isInvalidA) return 1; // Move A to the end
    if (isInvalidB) return -1; // Move B to the end
    if (feature === "race_pos" || feature === "quali_pos") {
      return valA - valB;
    } else {
      return valB - valA;
    }
  });
  const rank = sortedDrivers.findIndex(d => d.driver === driver) + 1;
  return rank || sortedDrivers.length + 1;
};


export const getPreviousRaceResult = (raceData, driver, feature, raceDates, n_past_race) => {
const currentDate = raceDates[n_past_race]
const value = raceData.filter(
  (entry) =>
    entry.driver_name === driver &&
    entry.race_date === currentDate
  )
if (value.length === 0) {
  return "-"
}
else if (feature === "fantasy_points") {
  return value[0].race_finish_points + value[0].race_stage_points
} else {
  return value[0][feature]
}
};

// 📌 The season currently being raced, from next_race_data.json
// (before race 1 of a new season the previous season is still the one with data)
export const getCurrentSeason = (nextRaceData) =>
  nextRaceData.next_race_number === 1
    ? nextRaceData.next_race_season - 1
    : nextRaceData.next_race_season;

// 📌 Utility function to get the last available race number based on last_race_data.json
export const getLastAvailableRaceNumber = (year, lastRaceData) => {
  if (year === lastRaceData.last_race_season) {
    return lastRaceData.last_race_number;
  }
  // For previous years, return race 36 (full season)
  return 36;
};

// 📌 Utility function to get the most recent available race data
export const getLastAvailableRaceFromData = (raceData, lastRaceData) => {
  // For current season, use last_race_data to determine latest available race
  const currentSeason = lastRaceData.last_race_season;
  const latestRaceNumber = lastRaceData.last_race_number;
  
  // Filter to get races up to the last available race
  return raceData.filter(race => 
    race.season_year === currentSeason && race.race_number <= latestRaceNumber
  );
};

// 📌 Utility function to get the latest race number from race data using last_race_data
export const getLatestRaceNumberFromData = (raceData, lastRaceData, seasonYear = null) => {
  if (!lastRaceData) {
    // Fallback to old logic if lastRaceData is not available
    console.warn("lastRaceData not available, falling back to race_number based logic");
    return Math.max(...raceData.map(r => r.race_number));
  }
  
  const targetYear = seasonYear || lastRaceData.last_race_season;
  return getLastAvailableRaceNumber(targetYear, lastRaceData);
};

// 📌 Utility function to check if race data is available for selection
export const isRaceDataAvailable = (race, lastRaceData) => {
  if (!lastRaceData || Object.keys(lastRaceData).length === 0) {
    // Fallback to date logic when lastRaceData is not available
    return new Date(race.race_date) <= new Date();
  }
  
  // Use existing getLastAvailableRaceNumber logic
  const lastAvailableRace = getLastAvailableRaceNumber(race.season_year, lastRaceData);
  return race.race_number <= lastAvailableRace;
};
